import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { usePosts } from '../../hooks/useSupabaseContent';
import { useCanonicalUrl } from '../../hooks/useCanonicalUrl';
import LoadingSpinner from '../ui/LoadingSpinner';
import Breadcrumbs, { BreadcrumbItem } from '../layout/Breadcrumbs';
import { useNavigate } from 'react-router-dom';
import '../../styles/components/masonry-grid.css';

interface PostCardProps {
  post: {
    id: string;
    title: string;
    description: string;
    content: string;
    route?: string;
    category: string;
    is_published: boolean;
    public_visibility: boolean;
    created_at: string;
    updated_at: string;
  };
}

const PostCard: React.FC<PostCardProps> = ({ post }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (post.route) {
      navigate(post.route, {
        state: { fromCategory: true }
      });
    }
  };

  const isClickable = !!post.route;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div 
      className={`card post-card ${isClickable ? 'clickable' : ''}`}
      onClick={isClickable ? handleClick : undefined}
      style={{ 
        cursor: isClickable ? 'pointer' : 'default',
        transition: 'all 0.3s ease'
      }}
    >
      <div className="card-content">
        <h3 className="card-title">{post.title}</h3>
        {post.description && (
          <p className="card-description">{post.description}</p>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
          <span className="post-date" style={{
            color: '#a0a0a0',
            fontSize: '0.85em'
          }}>
            {formatDate(post.created_at)}
          </span>
          {isClickable && (
            <div className="card-arrow">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path 
                  d="M6 4L10 8L6 12" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CategoryPosts: React.FC = () => {
  const { category } = useParams<{ category: string }>();
  const { data: allPosts, loading: postsLoading } = usePosts();

  // Filter posts by category
  const categoryPosts = useMemo(() => {
    if (!allPosts || !category) return [];
    
    const filtered = allPosts.filter(post => 
      post.category?.toLowerCase() === category.toLowerCase() && 
      post.is_published && 
      post.public_visibility
    );
    
    return filtered;
  }, [allPosts, category]);

  // Create breadcrumbs
  const breadcrumbs = useMemo(() => {
    const items: BreadcrumbItem[] = [
      { label: 'Home', path: '/' },
    ];
    
    if (category) {
      const categoryLabel = category.charAt(0).toUpperCase() + category.slice(1);
      items.push({ label: categoryLabel, isActive: true });
    } else {
      items.push({ label: 'Category Not Found', isActive: true });
    }
    
    return items;
  }, [category]);

  // Set canonical URL
  useCanonicalUrl({
    title: category ? `${category.charAt(0).toUpperCase() + category.slice(1)} Posts | LUT Rolls` : 'Category | LUT Rolls',
    description: category ? `Browse all posts in the ${category} category.` : 'Browse posts by category.',
    path: `/${category || 'category'}`
  });

  // Mobile detection for layout switching
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper function to distribute items across columns for grid layout
  const distributeItemsInColumns = useMemo(() => {
    if (isMobile || categoryPosts.length === 0) return [[], []];
    
    const column1: typeof categoryPosts = [];
    const column2: typeof categoryPosts = [];
    
    // Distribute items alternately between columns
    categoryPosts.forEach((post, index) => {
      if (index % 2 === 0) {
        column1.push(post);
      } else {
        column2.push(post);
      }
    });
    
    return [column1, column2];
  }, [categoryPosts, isMobile]);

  // Show loading when we don't have any posts data yet
  if (postsLoading && !allPosts) {
    return (
      <div className="page-container">
        <main className="page-main">
          <LoadingSpinner size="large" message="Loading posts..." />
        </main>
      </div>
    );
  }

  // Show category not found if category param is missing
  if (!category) {
    return (
      <div className="page-container">
        <main className="page-main">
          <div className="page-content">
            <Breadcrumbs items={breadcrumbs} />
            <header className="page-header">
              <h1>Category Not Found</h1>
              <div className="page-meta">
                <p>The category you're looking for doesn't exist.</p>
              </div>
            </header>
          </div>
        </main>
      </div>
    );
  }

  const categoryLabel = category.charAt(0).toUpperCase() + category.slice(1);

  return (
    <div className="page-container">
      <main className="page-main">
        <div className="page-content">
          <Breadcrumbs items={breadcrumbs} />
          
          <header className="page-header">
            <h1>{categoryLabel} Posts</h1>
            <div className="page-meta">
              <p>
                {categoryPosts.length > 0 
                  ? `${categoryPosts.length} post${categoryPosts.length === 1 ? '' : 's'} in the ${categoryLabel} category`
                  : `No posts found in the ${categoryLabel} category`
                }
              </p>
            </div>
          </header>

          <div className="posts-content">
            {postsLoading && (
              <div className="loading-card">
                <div className="card-content">
                  <p>Loading posts...</p>
                </div>
              </div>
            )}

            {!postsLoading && categoryPosts.length === 0 && (
              <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
                <div className="card-content">
                  <h3>No Posts Available</h3>
                  <p>No published posts found in the {categoryLabel} category.</p>
                  <p style={{ fontSize: '0.9em', opacity: 0.7 }}>
                    Posts will appear here once they are added to this category.
                  </p>
                </div>
              </div>
            )}

            {categoryPosts.length > 0 && (
              <>
                {isMobile ? (
                  // Mobile: Use simple vertical layout
                  <div className="mobile-cards-container">
                    {categoryPosts.map((post) => (
                      <div key={post.id} className="mobile-card-wrapper">
                        <PostCard post={post} />
                      </div>
                    ))}
                  </div>
                ) : (
                  // Desktop/Tablet: CSS masonry layout
                  <div className="css-masonry-container">
                    <div className="css-masonry-column">
                      {distributeItemsInColumns[0].map((post) => (
                        <div key={post.id} className="css-masonry-item">
                          <PostCard post={post} />
                        </div>
                      ))}
                    </div>
                    <div className="css-masonry-column">
                      {distributeItemsInColumns[1].map((post) => (
                        <div key={post.id} className="css-masonry-item">
                          <PostCard post={post} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default CategoryPosts;